<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  status: {
    type: String,
    default: null,
  },
  processingError: {
    type: String,
    default: null,
  },
  processingProgress: {
    type: Number,
    default: null,
  },
  showRetry: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['retry']);
const { t } = useI18n();

const isFailed = computed(() => props.status === 'failed');

const progress = computed(() => {
  // null/undefined means the doc predates progress tracking — treat as done,
  // not as 0% (Number(null) === 0 would wrongly render "Processing 0%").
  if (
    props.processingProgress === null ||
    props.processingProgress === undefined
  ) {
    return null;
  }
  const value = Number(props.processingProgress);
  if (!Number.isFinite(value)) return null;
  return Math.min(100, Math.max(0, Math.round(value)));
});

// A document is still being vectorized while it has a known progress below 100;
// status flips to available right after the upload step, so we key off progress.
const isInProgress = computed(
  () =>
    !isFailed.value &&
    (props.status === 'in_progress' ||
      (progress.value !== null && progress.value < 100))
);

const label = computed(() => {
  if (isFailed.value) return t('CAPTAIN.DOCUMENTS.PROCESSING_STATUS.FAILED');
  if (isInProgress.value) {
    return t('CAPTAIN.DOCUMENTS.PROCESSING_STATUS.IN_PROGRESS');
  }
  return null;
});

const progressLabel = computed(() =>
  t('CAPTAIN.DOCUMENTS.PROCESSING_STATUS.PROGRESS_PERCENT', {
    progress: progress.value ?? 0,
  })
);

const fullLabel = computed(() => {
  if (isFailed.value && props.processingError) {
    return `${label.value}: ${props.processingError}`;
  }
  if (isInProgress.value) {
    return `${label.value} ${progressLabel.value}`;
  }
  return label.value;
});

const textClass = computed(() => {
  if (isFailed.value) return 'text-n-ruby-11';
  return 'text-n-slate-11';
});
</script>

<template>
  <div v-if="label" class="flex flex-col gap-1" :title="fullLabel">
    <template v-if="isInProgress">
      <div
        class="flex items-center justify-between gap-2 text-xs"
        :class="textClass"
      >
        <span>{{ label }}</span>
        <span class="tabular-nums">{{ progressLabel }}</span>
      </div>
      <div
        class="relative h-1.5 w-full overflow-hidden rounded-full bg-n-alpha-2"
      >
        <div
          class="absolute inset-y-0 start-0 rounded-full bg-n-amber-9 transition-all duration-500"
          :style="{ width: `${progress}%` }"
        />
      </div>
    </template>
    <div
      v-else-if="isFailed"
      class="flex gap-1.5 items-center text-sm"
      :class="textClass"
    >
      <Icon icon="i-lucide-circle-alert" class="shrink-0 size-3.5" />
      <span class="truncate">{{ label }}</span>
      <Button
        v-if="showRetry"
        :label="t('CAPTAIN.DOCUMENTS.RETRY.BUTTON')"
        xs
        link
        ruby
        icon="i-lucide-refresh-cw"
        class="hover:!no-underline !gap-1 ms-1"
        @click.stop="emit('retry')"
      />
    </div>
  </div>
</template>
